import React, { useState } from 'react';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Tabs, Tab, Box } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DecFormValidation from './DecFormValidation';
import { apiFetchResponse } from '../lib/apiFetch';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

const StartDecButton = ({ funeralId }: { funeralId: string | number }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [liveURL, setLiveURL] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [screenshots, setScreenshots] = useState<Array<{
    page_url: string;
    screenshot: string;
    timestamp: number;
  }> | null>(null);

  const handleOpen = () => {
    setOpen(true);
    setLiveURL(null);
    setSessionId(null);
    setError(null);
    setTabValue(0); // Reset to validation tab
    setScreenshots(null);
  };

  const handleClose = () => {
    setOpen(false);
    setLiveURL(null);
    setSessionId(null);
    setError(null);
    setTabValue(0);
    setScreenshots(null);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleStartDec = async () => {
    setLoading(true);
    setError(null);
    setLiveURL(null);
    setSessionId(null);
    setScreenshots(null);
    try {
      const response = await apiFetchResponse('browser/populate-form', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          url: 'https://c234-174-89-238-216.ngrok-free.app/ExtranetCreate',
          funeralId,
        }),
      });
      const data = await response.json();
      if (data.success) {
        if (data.liveURL) {
          setLiveURL(data.liveURL);
          setSessionId(data.sessionId);
        }
        if (data.screenshots) {
          setScreenshots(data.screenshots);
        }
        setTabValue(1); // Switch to automation tab
      } else {
        setError(data.message || data.error || 'Unknown error');
      }
    } catch (err: any) {
      setError(err.message || 'Request failed');
    }
    setLoading(false);
  };

  const handleCancelSession = async () => {
    if (!sessionId) return;
    try {
      await apiFetchResponse('browser/cancel-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId }),
      });
    } catch (err) {
      // Optionally handle error
    }
    handleClose();
  };

  const handleTestExtension = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch real funeral data from the new endpoint
      const response = await apiFetchResponse(`browser/extension-form-data/${funeralId}`);
      const result = await response.json();
      
      if (result.success) {
        // Send the real data to the Chrome extension
        console.log('sending result', result.data);
        window.postMessage({
          type: 'EXTENSION_START_FORM_FILL',
          data: result.data
        }, '*');
      } else {
        setError(result.message || 'Failed to fetch funeral data');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch funeral data');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        onClick={handleOpen}
        startIcon={<PlayArrowIcon />}
        aria-label="Validate Fields"
        color="primary"
        variant="contained"
      >
        Validate Fields
      </Button>
      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogTitle>Validate Fields</DialogTitle>
        <DialogContent>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="DEC tabs">
              <Tab label="Field Validation" {...a11yProps(0)} />
              <Tab label="Automation" {...a11yProps(1)} />
            </Tabs>
          </Box>
          
          <TabPanel value={tabValue} index={0}>
            <DecFormValidation funeralId={funeralId} />
          </TabPanel>
          
          <TabPanel value={tabValue} index={1}>
            {loading ? (
              <CircularProgress />
            ) : error ? (
              <div style={{ color: 'red' }}>{error}</div>
            ) : (
              <div>
                {screenshots && (
                  <div style={{ marginBottom: '20px' }}>
                    <h3 style={{ margin: '0 0 16px 0', color: '#2e7d32' }}>
                      ✅ Form Automation Completed Successfully!
                    </h3>
                    <p style={{ margin: '0 0 20px 0', fontSize: '16px', color: '#555' }}>
                      Processed {screenshots.length} page{screenshots.length > 1 ? 's' : ''} and captured screenshots after submission:
                    </p>
                  </div>
                )}
                {screenshots && screenshots
                  .sort((a, b) => a.timestamp - b.timestamp)
                  .map((screenshot, index) => (
                  <div key={index} style={{ marginBottom: '30px', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '16px' }}>
                    <h4 style={{ margin: '0 0 8px 0', color: '#1976d2' }}>
                      Page {screenshot.timestamp + 1}: {screenshot.page_url}
                    </h4>
                    <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#666' }}>
                      Screenshot taken after form submission
                    </p>
                    <img 
                      src={`data:image/png;base64,${screenshot.screenshot}`} 
                      alt={`Form submission screenshot - ${screenshot.page_url}`} 
                      style={{ 
                        maxWidth: '100%', 
                        height: 'auto', 
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }} 
                    />
                  </div>
                ))}
                {liveURL ? (
                  <div>
                    <p>Session started! Click below to open:</p>
                    <a href={liveURL} target="_blank" rel="noopener noreferrer">
                      Open Live DEC Session
                    </a>
                  </div>
                ) : !screenshots ? (
                  <div>
                    <p>Do you want to start a DEC session?</p>
                    <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                      <p style={{ margin: '0 0 8px 0', fontWeight: 'bold', fontSize: '14px' }}>Quick Links:</p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <a 
                          href="https://services.etatcivil.gouv.qc.ca/extranetthanatologues/Depart.aspx" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ color: '#1976d2', textDecoration: 'none', fontSize: '14px' }}
                        >
                          📋 Extranet Home
                        </a>
                        <a 
                          href="https://services.etatcivil.gouv.qc.ca/extranetthanatologues/CreerDossier.aspx" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ color: '#1976d2', textDecoration: 'none', fontSize: '14px' }}
                        >
                          ➕ New DEC
                        </a>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p>Form automation completed successfully!</p>
                )}
              </div>
            )}
          </TabPanel>
        </DialogContent>
        <DialogActions>
          {tabValue === 0 && (
            <>
              <Button onClick={handleClose} color="secondary">
                Cancel
              </Button>
              <Button onClick={() => setTabValue(1)} color="primary" variant="contained">
                Proceed to Automation
              </Button>
            </>
          )}
          
          {tabValue === 1 && !liveURL && !loading && !screenshots && (
            <>
              <Button onClick={() => setTabValue(0)} color="secondary">
                Back to Validation
              </Button>
              <Button onClick={handleClose} color="secondary">
                Cancel
              </Button>
              <Button onClick={handleTestExtension} color="primary" variant="contained">
                Send to Extension
              </Button>

            </>
          )}
          
          {tabValue === 1 && (liveURL || screenshots) && (
            <>
              <Button onClick={() => setTabValue(0)} color="secondary">
                Back to Validation
              </Button>
              <Button onClick={handleClose} color="primary">
                Close
              </Button>
              {liveURL && (
                <Button onClick={handleCancelSession} color="secondary" variant="outlined">
                  Cancel Session
                </Button>
              )}
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default StartDecButton; 